import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useGetSectionOrders } from "../../../hooks/api/queries/useSection";
import { useParams, useSearchParams } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { cleanObject, formatDateYYYYMMDD } from "../../../helpers";
import { useWebSocketEventHandler } from "../../../hooks/useWebSocketEvents";
import { useCentrifugeChannel } from "../../../hooks/useCentrifugeChannel";

const useStaffSection = () => {
  const { section_id } = useParams();
  const [search, setSearch] = useSearchParams();
  const { wsEvents, clearWsEvent } = useWebSocketEventHandler();
  const profileDetails = useSelector(
    (state) => state.profileDetailsReducer.profileDetails,
  );
  const { ref: autoScrollRef, inView } = useInView();

  // Subscribe to the currently selected store's WS channels.
  // Publications with data.type are routed to Redux by useWebSocketManager.
  const storeId = search.get("store_id");
  const wsEnvMap = { develop: "dev", production: "prod" };
  const wsEnv =
    wsEnvMap[process.env.REACT_APP_ENV] ?? process.env.REACT_APP_ENV;
  useCentrifugeChannel(storeId ? `${wsEnv}_store_${storeId}` : null);
  useCentrifugeChannel(storeId ? `critical:${wsEnv}_store_${storeId}` : null);

  // Silently drop section_item_completed events that the current user triggered.
  // isOwnCompletion is computed synchronously so sectionItemCompletedEvent is
  // already null on the first render for own events — prevents the sound from
  // firing before the async useEffect cleanup can run.
  const rawCompletedEvent = wsEvents?.section_item_completed ?? null;
  const isOwnCompletion =
    rawCompletedEvent?.payload?.completed_by === profileDetails?.id;
  const sectionItemCompletedEvent = isOwnCompletion ? null : rawCompletedEvent;
  useEffect(() => {
    if (rawCompletedEvent && isOwnCompletion) {
      clearWsEvent({ eventType: "section_item_completed" });
    }
  }, [rawCompletedEvent]);
  const [source, setSource] = useState();
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("kitchenDisplayViewMode") || "grid",
  );

  const queries = (() => {
    let params = `?section_id=${section_id}`;
    for (const entry of search.entries()) {
      params = `${params}&${entry[0]}=${entry[1]}`;
    }
    return params;
  })();

  useEffect(() => {
    if (!search.has("status")) {
      setSearch(
        new URLSearchParams(
          cleanObject({
            store_id: search.get("store_id"),
            status: "RECEIVED",
            start_date: formatDateYYYYMMDD(new Date()),
            end_date: formatDateYYYYMMDD(new Date()),
          }),
        ),
        { replace: true },
      );
    }
  }, []);

  const {
    isLoading: isfetchingOrders,
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetSectionOrders(queries);

  const sectionItems = new Map();
  // eslint-disable-next-line array-callback-return
  data?.pages?.map((page) => {
    sectionItems.set(`${page.meta.current_page}`, page.data);
  });
  const sections = [].concat(...sectionItems.values());

  useEffect(() => {
    if (inView && hasNextPage) fetchNextPage();
  }, [inView, fetchNextPage, hasNextPage]);

  const setDateFilter = (data) => {
    search.set("start_date", data.startDate);
    search.set("end_date", data.endDate);
    search.delete("page");
    setSearch(search, { replace: true });
  };

  const setSourceFilter = (value) => {
    if (value === "ALL") search.delete("order_source");
    else search.set("order_source", value || source);
    search.delete("page");
    setSearch(search, { replace: true });
  };

  const options = [
    { name: "All", id: "ALL" },
    { id: "INTERNAL", name: "Point of Sales" },
    { id: "STOREFRONT", name: "Online Orders" },
  ];

  useEffect(() => {
    const savedSource = localStorage.getItem("selectedOrderSource");
    if (savedSource) {
      setSource(savedSource);
    }
  }, []);

  const handleSetViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("kitchenDisplayViewMode", mode);
  };

  return {
    isfetchingOrders,
    isFetchingNextPage,
    search,
    autoScrollRef,
    sectionOrders: sections || [],
    setDateFilter,
    setSourceFilter,
    options,
    source,
    setSource,
    viewMode,
    setViewMode: handleSetViewMode,
    sectionItemsCreatedEvent: wsEvents?.section_items_created ?? null,
    clearSectionItemsEvent: () =>
      clearWsEvent({ eventType: "section_items_created" }),
    sectionItemCompletedEvent,
    clearSectionItemCompletedEvent: () =>
      clearWsEvent({ eventType: "section_item_completed" }),
  };
};

export default useStaffSection;
